import React from 'react';
import dynamic from 'next/dynamic';
import { MOBILE_THRESHOLD } from '@scale/frontend-lib/util';
import { GlobalCSSFontsLoading } from './global_css_fonts';

export const Colors = {
  ScaleBlue: '#0076FF',
  ScaleRed: '#EF3D57',
  ScaleYellow: '#FFCA28',
  ScaleGreen: '#72CE7B',
  RemoBlue: '#2B99D8', // Buttons and links
  LightSkyBlue: '#BBDEF2',
  RemoGreen: '#29CD9C',
  RemoGreenLight: '#C1EAD9',
  RemoGold: '#F8BB06', // Warnings
  RemoOrange: '#F39212', // Warnings
  RemoRed: '#E8412A', // Errors
  RemoGray01: '#F8F8F8', // Background wash
  RemoGray05: '#F2F2F2', // Secondary wash
  RemoGray10: '#E6E6E6', // Borders
  RemoGray20: '#CCCCCC', // Disabled buttons
  RemoGray50: '#808080', // Secondary buttons
  RemoGray75: '#404040', // Text
  TrueGray50: '#FAFAFA',
  Teal500: '#00A4C3',
};

const GlobalCSSFonts = dynamic(
  () => {
    return import('./global_css_fonts');
  },
  // eslint-disable-next-line react/display-name
  { loading: () => <GlobalCSSFontsLoading />, ssr: false },
);

const GlobalCSS = () => (
  <>
    <style jsx global>{`
      :root {
        /* Remotasks colors */
        --color-ScaleBlue: ${Colors.ScaleBlue};

        --color-RemoBlue: ${Colors.RemoBlue};
        --color-RemoGreen: ${Colors.RemoGreen};
        --color-RemoGreenLight: ${Colors.RemoGreenLight};
        --color-RemoGold: ${Colors.RemoGold};
        --color-RemoRed: ${Colors.RemoRed};
        --color-RemoGray01: ${Colors.RemoGray01};
        --color-RemoGray05: ${Colors.RemoGray05};
        --color-RemoGray10: ${Colors.RemoGray10};
        --color-RemoGray20: ${Colors.RemoGray20};
        --color-RemoGray50: ${Colors.RemoGray50};
        --color-RemoGray75: ${Colors.RemoGray75};
        --color-TrueGray50: ${Colors.TrueGray50};

        /* Design System 2.0 Colors */
        --color-LightMauve: #d1aad7;
        --color-LightPeriwinkle: #7b8fdd;
        --color-LightSkyBlue: #bbdef2;
        --color-Mauve: #d1aad7;
        --color-Periwinkle: #6b80d2;
        --color-SkyBlue: #6b80d2;

        --color-Periwinkle100: #6a5df9;
        --color-Periwinkle75: #8f86fa;
        --color-Periwinkle50: #b4aefc;
        --color-Periwinkle25: #dad7fd;
        --color-Periwinkle10: #f0effe;

        /* Marketing & customer dashboard colors */
        --text-color: var(--color-RemoGray75);
        --border-radius: 8px;
        --pill-border-radius: 12px;
        --link-color: var(--color-RemoBlue);
        --bg-gray-light: #f5f4f9;
        --bg-gray-light-1: var(--bg-gray-light);
        --bg-gray-light-2: #f6f6f6;
        --bg-gray-light-3: #f5f9fa;
        --border-gray-light: var(--color-RemoGray10);
        --box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.2);
        --box-shadow-hover: 0 10px 40px -10px rgba(0, 0, 0, 0.4);
        --box-shadow-long: 0 50px 100px -30px rgba(0, 0, 0, 0.5);
        --color-primary: var(--color-RemoBlue);
        --color-accept: var(--color-RemoGreen);
        --color-reject: var(--color-RemoRed);
        --color-grey-text: var(--color-RemoGray75);
        --color-sensor-fusion-cuboids: #9631e7;
        --color-sensor-fusion-segmentation: #29d683;
        --color-semantic-segmentation: #e23e7c;
        --color-cuboids: #3cc0a7;
        --color-polygons: #ff7600;
        --color-twod-boxes: #63b31c;
        --color-lines-splines: #1f77e0;
        --color-video-box-annotation: #ffbb1a;
        --color-transcription: #ff866e;
        --color-ocr-transcription: #29d683;
        --color-categorization: #ff8fb1;
        --color-data-collection: #4a7afa;
        --color-point-annotation: #00b7ff;
        --color-black: #000;
        --color-orange: #fd9d52;
        --color-pink: #fd62e9;
        --color-gold: rgba(255, 202, 40, 1);
        --color-comparison: #da57c8;
        --color-image-recognition: #ed72e2;
        --color-video: #ffbb1a;
        --color-dashboard-1: #271938;
        --color-dashboard-2: #ff9bee;
        --color-dashboard-3: #503676;

        --font-family-code: 'Plex Mono', Menlo, Consolas, Monaco, Lucida Console, Courier New,
          monospace, serif;

        /* Grid */
        --grid-gap: 40px;
        --section-padding: 40px 0 40px 0;

        /* Typography */
        --h1-font-size: 30px;

        --h2-font-size: 30px;

        --h3-font-size: 24px;

        --h4-font-size: 20px;

        /* Forms */
        --bg-form-input: var(--bg-gray-light);
        --color-form-input: #636e7d;
        --bg-form-focus: rgb(237, 235, 244);
        --form-transition: 0.3s ease all;
      }
      @media only screen and (min-width: ${MOBILE_THRESHOLD}) {
        :root {
          --grid-gap: 32px;
          --section-padding: 100px 0 100px 0;
        }
      }
      @media (min-width: ${MOBILE_THRESHOLD}) {
        :root {
          --h1-font-size: 42px;
        }
      }
      @media (min-width: ${MOBILE_THRESHOLD}) {
        :root {
          --h2-font-size: 36px;
        }
      }
      @media (min-width: ${MOBILE_THRESHOLD}) {
        :root {
          --h3-font-size: 28px;
        }
      }
      @media (min-width: ${MOBILE_THRESHOLD}) {
        :root {
          --h4-font-size: 24px;
        }
      }

      @font-face {
        font-family: 'Plex Mono';
        font-display: auto;
        src: url('/static/global/fonts/plex-mono/IBMPlexMono-Regular.eot?#iefix');
        src: url('/static/global/fonts/plex-mono/IBMPlexMono-Regular.eot?#iefix') format('eot'),
          url('https://fonts.gstatic.com/s/ibmplexmono/v5/-F63fjptAgt5VM-kVkqdyU8n1i8q131nj-o.woff2')
            format('woff2'),
          url('/static/global/fonts/plex-mono/IBMPlexMono-Regular.woff') format('woff'),
          url('/static/global/fonts/plex-mono/IBMPlexMono-Regular.ttf') format('truetype');
      }
      @font-face {
        font-family: 'Plex Mono';
        font-display: auto;
        src: url('/static/global/fonts/plex-mono/IBMPlexMono-Medium.eot?#iefix');
        src: url('/static/global/fonts/plex-mono/IBMPlexMono-Medium.eot?#iefix') format('eot'),
          url('https://fonts.gstatic.com/s/ibmplexmono/v5/-F6qfjptAgt5VM-kVkqdyU8n3twJwlBFgsAXHNk.woff2')
            format('woff2'),
          url('/static/global/fonts/plex-mono/IBMPlexMono-Medium.woff') format('woff'),
          url('/static/global/fonts/plex-mono/IBMPlexMono-Medium.ttf') format('truetype');
        font-weight: 500;
      }

      @font-face {
        font-family: 'JetBrains Mono';
        font-display: auto;
        src: url('/static/internal/fonts/JetBrainsMono-Regular.woff2') format('woff2'),
          url('/static/internal/fonts/JetBrainsMono-Regular.woff') format('woff');
        font-weight: 400;
      }

      @font-face {
        font-family: 'JetBrains Mono';
        font-display: auto;
        src: url('/static/internal/fonts/JetBrainsMono-Medium.woff2') format('woff2'),
          url('/static/internal/fonts/JetBrainsMono-Medium.woff') format('woff');
        font-weight: 500;
      }

      @font-face {
        font-family: 'Inter';
        font-weight: 400;
        font-display: auto;
        src: url('/static/global/fonts/inter/Inter-Regular.woff2') format('woff2'),
          url('/static/global/fonts/inter/Inter-Regular.woff') format('woff');
      }

      @font-face {
        font-family: 'Inter';
        font-weight: 500;
        font-display: auto;
        src: url('/static/global/fonts/inter/Inter-Medium.woff2') format('woff2'),
          url('/static/global/fonts/inter/Inter-Medium.woff') format('woff');
      }

      body {
        font-size: 14px;
        font-family: var(--font-family);
        text-rendering: geometricPrecision;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        font-feature-settings: 'pnum';
        font-variant-numeric: proportional-nums;
        font-weight: normal;
        min-width: 320px;
        margin: 0;
        padding: 0;
        font-stretch: normal;
        font-style: normal;
      }

      h1,
      h2,
      h3,
      h4,
      h5 {
        color: black;
      }

      button,
      input {
        font-family: var(--font-family);
      }

      a,
      .anchor {
        color: var(--color-RemoBlue);
        font-weight: 500;
        text-decoration: none;
        outline: none;
        cursor: pointer;
      }

      li,
      p {
        line-height: 2;
      }

      table {
        border-collapse: collapse;
        padding: 0;
      }

      img,
      svg {
        max-width: 100%;
      }

      img {
        display: block;
      }

      *:focus {
        outline: 0;
      }

      code,
      pre {
        font-family: var(--font-family-code);
      }

      /* A few tasks contain a duplicate iframe in the instructions field; only one should be displayed */
      iframe + iframe {
        display: none;
      }

      /* ---------- Tooltips ------------------------------------------------ */

      .__react_component_tooltip {
        user-select: none;
        z-index: 2000;
      }

      .__react_component_tooltip.type-dark.show {
        opacity: 1 !important;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.02), 0 0 2px rgba(0, 0, 0, 0.02) !important;
        padding: 12px !important;
        line-height: 1.4em;
        font-weight: bold;
      }

      .__react_component_tooltip.type-dark.show.place-top:after {
        display: none;
      }

      .__react_component_tooltip.type-light.show {
        opacity: 1 !important;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05), 0 0 2px rgba(0, 0, 0, 0.05) !important;
        padding: 12px !important;
        line-height: 1.4em;
        font-weight: bold;
      }

      .__react_component_tooltip.type-light.show.place-top:after {
        display: none;
      }

      /* ---------- Text ---------------------------------------------------- */

      .remo-label {
        font-size: 13px;
        font-weight: 500;
      }

      .remo-label em {
        font-style: normal;
        opacity: 0.4;
      }

      /*-------- Recaptcha ----------------------------------------------------*/
      .grecaptcha-badge {
        display: none !important;
      }

      .ql-editor img {
        display: inline-block;
      }
    `}</style>
    <GlobalCSSFonts />
  </>
);

export default GlobalCSS;
